"use client";

// Die Hilfe zum Lesen und Suchen.
//
// Die Fragen stehen als details/summary da und funktionieren ohne Skript.
// Das Skript tut zweierlei: Es filtert die Kästen nach dem, was ins
// Suchfeld getippt wird, und es klappt die Frage auf, auf die ein Link mit
// Raute zeigt — sonst stünde der Besucher vor einem geschlossenen Kasten.
//
// Die Antworten kommen fertig gerendert vom Server (React-Knoten aus
// lib/hilfe.tsx). Für die Suche wird ihr Text einmal nach dem Einhängen aus
// dem DOM gelesen — so muss niemand jede Antwort doppelt pflegen, einmal
// als Markup und einmal als Suchtext.
import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { ChevronDown, Search, X } from "lucide-react";
import type { Rubrik } from "../../lib/hilfe";

export default function HilfeListe({ rubriken }: { rubriken: Rubrik[] }) {
  const [suche, setSuche] = useState("");
  const [texte, setTexte] = useState<Record<string, string>>({});
  const kaesten = useRef<Map<string, HTMLDetailsElement>>(new Map());

  // Suchtext je Frage: Überschrift plus Antwort, beim ersten Tippen aus dem
  // DOM eingesammelt — nicht beim Einhängen, damit die Seite ohne Suche
  // nicht einmal mehr rendert als nötig.
  const tippen = (wert: string) => {
    if (!Object.keys(texte).length) {
      const t: Record<string, string> = {};
      kaesten.current.forEach((el, id) => {
        t[id] = (el.textContent ?? "").toLowerCase();
      });
      setTexte(t);
    }
    setSuche(wert);
  };

  // Ein Link auf #frage-… soll die Frage aufklappen, nicht nur hinscrollen.
  useEffect(() => {
    const oeffnen = () => {
      const id = decodeURIComponent(location.hash.slice(1));
      const el = id ? kaesten.current.get(id) : undefined;
      if (el) el.open = true;
    };
    oeffnen();
    window.addEventListener("hashchange", oeffnen);
    return () => window.removeEventListener("hashchange", oeffnen);
  }, []);

  const begriff = suche.trim().toLowerCase();
  const woerter = useMemo(() => begriff.split(/\s+/).filter(Boolean), [begriff]);

  const trifft = (id: string) => {
    if (!woerter.length) return true;
    const t = texte[id];
    if (t === undefined) return true;
    return woerter.every((w) => t.includes(w));
  };

  const sichtbar = rubriken.map((r) => ({
    rubrik: r,
    fragen: r.fragen.filter((f) => trifft(`${r.id}-${f.id}`)),
  }));
  const gesamt = sichtbar.reduce((n, s) => n + s.fragen.length, 0);

  return (
    <>
      <div className="vh-kopf">
        <label className="vh-suche">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={suche}
            onChange={(e) => tippen(e.target.value)}
            placeholder="Wonach suchst du? Etwa „Mikrofon“ oder „CUDA“"
            aria-label="Hilfe durchsuchen"
            autoComplete="off"
          />
          {suche && (
            <button type="button" className="vh-suche-leer" onClick={() => setSuche("")} aria-label="Suche leeren">
              <X aria-hidden="true" />
            </button>
          )}
        </label>

        <ul className="vh-rubriken" aria-label="Rubriken">
          {sichtbar.map(({ rubrik, fragen }) => (
            <li key={rubrik.id} hidden={!fragen.length}>
              <a href={`#${rubrik.id}`} style={{ "--c": rubrik.farbe } as React.CSSProperties}>
                <i aria-hidden="true" />
                {rubrik.titel}
                <small>{fragen.length}</small>
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="vh-body">
        {gesamt === 0 && (
          <p className="vh-nichts" role="status">
            Zu <b>„{suche.trim()}“</b> steht hier nichts. Schreib uns an{" "}
            <a href="mailto:hello@logsy.de">hello@logsy.de</a> — dann kommt die Antwort auch hierher.
          </p>
        )}

        {sichtbar.map(({ rubrik, fragen }) => (
          <section
            key={rubrik.id}
            id={rubrik.id}
            className="vh-rubrik"
            hidden={!fragen.length}
            style={{ "--c": rubrik.farbe } as React.CSSProperties}
          >
            <div className="vh-rubrik-head">
              <div className="vh-rubrik-art" aria-hidden="true">
                <Image src={rubrik.bild} alt="" fill sizes="140px" quality={90} />
              </div>
              <h2>{rubrik.titel}</h2>
              <p>{rubrik.satz}</p>
            </div>

            <div className="vh-fragen">
              {rubrik.fragen.map((f) => {
                const id = `${rubrik.id}-${f.id}`;
                return (
                  <details
                    key={id}
                    id={id}
                    className="vh-frage"
                    hidden={!fragen.includes(f)}
                    ref={(el) => {
                      if (el) kaesten.current.set(id, el);
                      else kaesten.current.delete(id);
                    }}
                  >
                    <summary>
                      {f.frage}
                      <ChevronDown aria-hidden="true" />
                    </summary>
                    <div className="vh-antwort">{f.antwort}</div>
                  </details>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
